import React, { useCallback, useMemo, useState } from "react";

import styles from "../../OrgSelector/OrgSelector.module.css";
import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  TouchableBox,
  useLayoutContext,
  useTheme,
} from "arkh";
import { graphql, useFragment, useMutation } from "react-relay";
import { TemplateSelectorApplyTemplateMutation } from "./__generated__/TemplateSelectorApplyTemplateMutation.graphql";
import { TemplateSelectorTemplateItem_TodoWorkspaceTemplate$key } from "./__generated__/TemplateSelectorTemplateItem_TodoWorkspaceTemplate.graphql";

import * as Select from "@radix-ui/react-select";

import { TemplateSelector_workspace$key } from "./__generated__/TemplateSelector_workspace.graphql";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { FormattedMessage, useIntl } from "react-intl";
import { useAlert } from "../../../../common/Alert";
import { MoreButton } from "../../../../common/MoreButton";
import { TemplateSelectorDeleteTemplateMutation } from "./__generated__/TemplateSelectorDeleteTemplateMutation.graphql";
import { TemplateSelector_todo$key } from "./__generated__/TemplateSelector_todo.graphql";

import cuid from "cuid";
import { TemplateSelectorCreateTemplatePopup } from "./TemplateSelectorCreateTemplatePopup";
import { TemplateSelectorUpdateTemplatePopup } from "./TemplateSelectorUpdateTemplatePopup";

export const TemplateChooser = (props: {
  workspace: TemplateSelector_workspace$key;
  todoWorkspace: TemplateSelector_todo$key;
}) => {
  const todoWorkspace = useFragment(
    graphql`
      fragment TemplateSelector_todo on TodoWorkspace {
        id
        updatedBy {
          __typename
        }
        body

        title

        descriptionFiles {
          __typename
        }
        descriptionImages {
          __typename
        }

        template {
          id
          name
          ...TemplateSelectorTemplateItem_TodoWorkspaceTemplate
        }
      }
    `,
    props.todoWorkspace
  );

  const isEmpty = useMemo(
    () =>
      (todoWorkspace.body === null || todoWorkspace.body.trim().length === 0) &&
      todoWorkspace.descriptionFiles.length === 0 &&
      todoWorkspace.descriptionImages.length === 0,
    [
      todoWorkspace.body,
      todoWorkspace.descriptionFiles.length,
      todoWorkspace.descriptionImages.length,
    ]
  );

  const workspace = useFragment(
    graphql`
      fragment TemplateSelector_workspace on Workspace {
        id
        templates {
          id

          ...TemplateSelectorTemplateItem_TodoWorkspaceTemplate
        }
      }
    `,
    props.workspace
  );

  const [applyTemplate] = useMutation<TemplateSelectorApplyTemplateMutation>(
    graphql`
      mutation TemplateSelectorApplyTemplateMutation(
        $todoWorkspaceId: ID!
        $templateId: ID!
      ) {
        todoWorkspace(id: $todoWorkspaceId) {
          applyTemplate(templateId: $templateId) {
            id
            ...TemplateSelector_todo

            ...WorkspaceTodoEditMainSubTodos_todoWorkspace
            ...WorkspaceTodoEditMainProducts_TodoWorkspace
            ...WorkspaceTodoEditMainPrioritySelector_Todo
            ...WorkspaceTodoEditMainEditedInfo_Todo
            ...WorkspaceTodoEditMainContent_Todo
            ...WorkspaceTodoEditMainExpireSelector_Todo
            ...WorkspaceTodoEditMainResponseType_TodoWorkspace
            ...WorkspaceTodoEditMainPushNotification_todoWorkspace
            ...WorkspaceRecieversListItemButton_workspaceEntity
          }
        }
      }
    `
  );

  const [deleteTemplate] = useMutation<TemplateSelectorDeleteTemplateMutation>(
    graphql`
      mutation TemplateSelectorDeleteTemplateMutation($templateId: ID!) {
        todoWorkspaceTemplate(id: $templateId) {
          deleteTemplate {
            id @deleteRecord
          }
        }
      }
    `
  );

  const layout = useLayoutContext();
  const theme = useTheme();

  const Alert = useAlert();
  const intl = useIntl();

  const intl_tokens = useIntlTokens();

  const requestApplyTemplate = useCallback(
    (templateId: string) => {
      if (isEmpty) {
        applyTemplate({
          variables: {
            todoWorkspaceId: todoWorkspace.id,
            templateId,
          },
        });
      } else {
        Alert.alert(
          intl_tokens.ApplyTemplate,
          intl.formatMessage({
            defaultMessage:
              "Do you want to replace your current content with the selected template, including description, files and images?",
            description: "Workspace Todo Edit Apply Template Body",
          }),
          [
            {
              text: intl_tokens.Cancel,
              style: "cancel",
            },
            {
              text: intl_tokens.ApplyTemplate,
              style: "default",
              onPress: () => {
                applyTemplate({
                  variables: {
                    todoWorkspaceId: todoWorkspace.id,
                    templateId,
                  },
                });
              },
            },
          ]
        );
      }
    },
    [
      Alert,
      applyTemplate,
      intl,
      intl_tokens.ApplyTemplate,
      intl_tokens.Cancel,
      isEmpty,
      todoWorkspace.id,
    ]
  );

  const requestDeleteTemplate = useCallback(
    (templateId: string, templateName: string) => {
      Alert.alert(
        intl_tokens.DeleteTemplate,
        intl.formatMessage(
          {
            defaultMessage:
              "Do you want to delete the current template {templateName}. This action cannot be undone.",
            description: "Workspace Todo Edit Delete Template Body",
          },
          {
            templateName,
          }
        ),
        [
          {
            text: intl_tokens.Cancel,
            style: "cancel",
          },
          {
            text: intl_tokens.Delete,
            style: "destructive",
            onPress: () => {
              deleteTemplate({
                variables: {
                  templateId,
                },
              });
            },
          },
        ]
      );
    },
    [
      Alert,
      deleteTemplate,
      intl,
      intl_tokens.Cancel,
      intl_tokens.Delete,
      intl_tokens.DeleteTemplate,
    ]
  );

  const [requestingCreateTemplate, setRequestingCreateTemplate] = useState<
    null | string
  >(null);

  const [requestingUpdateTemplate, setRequestingUpdateTemplate] = useState<{
    id: string;
    name: string;
  } | null>(null);

  return (
    <>
      <TemplateSelectorCreateTemplatePopup
        templateName={requestingCreateTemplate}
        setTemplateName={setRequestingCreateTemplate}
        todoWorkspaceId={todoWorkspace.id}
      />
      <TemplateSelectorUpdateTemplatePopup
        template={requestingUpdateTemplate}
        todoWorkspaceId={todoWorkspace.id}
        onDismiss={() => {
          setRequestingUpdateTemplate(null);
        }}
      />
      <Select.Root
        value={todoWorkspace.template?.id ?? cuid()}
        onValueChange={(v) => {
          if (v === "CREATE_NEW") {
            setRequestingCreateTemplate(todoWorkspace.title);
          } else {
            requestApplyTemplate(v);
          }
        }}
      >
        <Select.Trigger
          className={styles.Trigger}
          style={{
            cursor: "pointer",

            border: `1px solid ${theme["border-primary"]}`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: layout.radius.medium,
            paddingLeft: layout.space.small,
            paddingRight: layout.space.small,
            lineHeight: 1,
            height: 30,
          }}
        >
          <Select.Value>
            {todoWorkspace.template ? (
              <SelectedTemplate title={todoWorkspace.template.name} />
            ) : null}
          </Select.Value>
          {todoWorkspace.template ? null : <NoSelectedTemplate />}
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            style={{
              cursor: "pointer",
              overflow: "hidden",
              backgroundColor: theme.surface,
              borderRadius: layout.radius.medium,
              zIndex: 99999,
              boxShadow:
                "0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)",
            }}
          >
            <Select.ScrollUpButton>
              <Box
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                spaceTop="xsmall"
              >
                <AHIcon type="chevron-up" />
              </Box>
            </Select.ScrollUpButton>
            <Select.Viewport>
              {workspace.templates
                .filter((f) => f?.id)
                .map((template) => (
                  <TemplateItem
                    isSelected={todoWorkspace.template?.id === template.id}
                    template={template}
                    key={template.id}
                  />
                ))}
              <AHSeparator />

              <Select.Item
                value={"CREATE_NEW"}
                className={styles.SelectItem}
                style={{
                  fontSize: 13,
                  lineHeight: 1,

                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  padding: "15px 20px 15px 20px",
                  position: "relative",
                  userSelect: "none",
                }}
              >
                <Box style={{ flex: 1 }}>
                  <Box
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flex: 1,
                    }}
                  >
                    <AHText spaceRight="large">
                      <FormattedMessage
                        description="TemplateSelectorCreateTemplate"
                        defaultMessage="Create new template from current content"
                      />
                    </AHText>
                  </Box>
                </Box>
              </Select.Item>
            </Select.Viewport>
            <Select.ScrollDownButton>
              <Box
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                spaceTop="xsmall"
              >
                <AHIcon type="chevron-down" />
              </Box>
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      <Box spaceLeft="small">
        {todoWorkspace.template ? (
          <MoreButton
            items={[
              {
                icon: "save-replace",
                label: intl_tokens.UpdateTemplate,
                onPress: () => {
                  if (todoWorkspace.template?.id) {
                    setRequestingUpdateTemplate({
                      id: todoWorkspace.template.id,
                      name: todoWorkspace.template.name,
                    });
                  }
                },
              },
              {
                icon: "trash",
                label: intl_tokens.DeleteTemplate,
                color: "negative",
                onPress: () => {
                  if (todoWorkspace.template?.id) {
                    requestDeleteTemplate(
                      todoWorkspace.template.id,
                      todoWorkspace.template.name
                    );
                  }
                },
              },
            ]}
          >
            {(op) => (
              <TouchableBox onPress={op}>
                <AHIcon type="more-vertical" color="text-secondary" />
              </TouchableBox>
            )}
          </MoreButton>
        ) : null}
      </Box>
    </>
  );
};

function NoSelectedTemplate() {
  const intl_tokens = useIntlTokens();
  return (
    <Box style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
      <AHIcon type="template" color="text-secondary" />
      <AHText spaceLeft="xsmall" type="subtitle" color="text-secondary">
        {intl_tokens.Templates}
      </AHText>
    </Box>
  );
}

function SelectedTemplate(props: { title: string }) {
  return (
    <Box style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
      <AHIcon type="template" color="text-secondary" />
      <AHText spaceLeft="xsmall" type="subtitle" color="text-secondary">
        {props.title || " - "}
      </AHText>
    </Box>
  );
}

function TemplateItem(props: {
  template: TemplateSelectorTemplateItem_TodoWorkspaceTemplate$key;
  isSelected?: boolean;
}) {
  const template = useFragment(
    graphql`
      fragment TemplateSelectorTemplateItem_TodoWorkspaceTemplate on TodoWorkspaceTemplate {
        id
        name
      }
    `,
    props.template
  );

  return (
    <Select.Item
      value={template.id}
      className={styles.SelectItem}
      style={{
        fontSize: 13,
        lineHeight: 1,

        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        padding: "15px 20px 15px 20px",
        position: "relative",
        userSelect: "none",
      }}
    >
      <Box style={{ flex: 1 }}>
        <Box
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <Select.ItemText>
            <AHText
              type={props.isSelected ? "subtitle" : "body"}
              color="text-primary"
              spaceRight="large"
            >
              {template.name || " - "}
            </AHText>
          </Select.ItemText>
        </Box>
      </Box>
    </Select.Item>
  );
}
